import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { Transaction, TransactionColumns } from "../../types";
import { BlockchainExplorerLink } from "~/components/BlockchainExplorerLink";
import { Emdash } from "~/components/Punctuation";
import { NetworkBadge } from "~/modules/assets/components/AssetTable/NetworkBadge";
import { Tooltip } from "~/components/Tooltip";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { Link } from "~/components/Link";
import { TransactionUserEvents } from "~/modules/analytics/events/transactions";
import { Icon } from "~/components/Icon";
import { getNetworkURL, getPlatformURL } from "~/routes";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { getAssetURL } from "~/utils/getAssetURL";

// // // //

// const TRANSACTION_TYPE_TO_ICON: { [key in TransactionTypes]: IconDefinition } =
//     {
//         [TransactionTypes.TokenTransfer]: faHandHoldingUsd,
//         [TransactionTypes.LoanInterestPaid]: faPiggyBank,
//         [TransactionTypes.TokenBurn]: faBurn,
//         [TransactionTypes.LoanFunded]: faDollarSign,
//         [TransactionTypes.LoanDefaulted]: faExclamationTriangle,
//         [TransactionTypes.LoanPrincipalPaid]: faCheckCircle,
//         [TransactionTypes.LoanPrincipalDecreased]: faArrowDown,
//         [TransactionTypes.PoolWithdrawal]: faHandHoldingWater,
//         [TransactionTypes.LoanPrincipalIncreased]: faArrowUp,
//         [TransactionTypes.TokenMint]: faPlusCircle,
//         [TransactionTypes.PoolDeposit]: faCoins,
//         [TransactionTypes.LoanFundsWithdrawn]: faHandHoldingUsd,
//         [TransactionTypes.ProtocolFeesPaid]: faPercent,
//     };

// const TRANSACTION_TYPE_TO_COLOR: { [key in TransactionTypes]: ColumnColors } = {
//     [TransactionTypes.TokenTransfer]: ColumnColors.Blue,
//     [TransactionTypes.LoanInterestPaid]: ColumnColors.Green,
//     [TransactionTypes.TokenBurn]: ColumnColors.Red,
//     [TransactionTypes.LoanFunded]: ColumnColors.Green,
//     [TransactionTypes.LoanDefaulted]: ColumnColors.Red,
//     [TransactionTypes.LoanPrincipalPaid]: ColumnColors.Green,
//     [TransactionTypes.LoanPrincipalDecreased]: ColumnColors.Yellow,
//     [TransactionTypes.PoolWithdrawal]: ColumnColors.Purple,
//     [TransactionTypes.LoanPrincipalIncreased]: ColumnColors.Green,
//     [TransactionTypes.TokenMint]: ColumnColors.Green,
//     [TransactionTypes.PoolDeposit]: ColumnColors.Blue,
//     [TransactionTypes.LoanFundsWithdrawn]: ColumnColors.Orange,
//     [TransactionTypes.ProtocolFeesPaid]: ColumnColors.None,
// };

export const ASSET_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Asset",
    slug: "asset_name",
    type: ColumnTypes.string,
    // width: 250,
    width: 150,
    render: (tx) => {
        if (!tx.asset) {
            return <Emdash />;
        }

        return (
            <Link
                href={getAssetURL({ asset: tx.asset })}
                variant="default"
                className="w-full flex items-center gap-1.5"
                analyticsLabel={TransactionUserEvents.ClickAssetLink}
            >
                <ImageLoader
                    src={tx.asset.icon_url || RWA_COIN_ICON}
                    alt={`${tx.asset.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                />
                <span className="dg-overflow">{tx.asset.name}</span>
            </Link>
        );
    },
};

export const TX_HASH_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Tx Hash",
    slug: TransactionColumns.transactionHash,
    sortsOn: TransactionColumns.transactionHash,
    type: ColumnTypes.string,
    width: 175,
    maxWidth: 175,
    render: (tx, { size }) => {
        if (!tx.asset) {
            return <Emdash />;
        }

        return (
            <BlockchainExplorerLink
                address={tx.transaction_hash}
                networkSlug={tx.token.network.slug}
                analyticsLabel={TransactionUserEvents.ClickTransactionHash}
                size={size}
                type="tx"
            />
        );
    },
};

export const TYPE_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Type",
    slug: "transaction_type_name",
    type: ColumnTypes.string,
    width: 200,
    render: (tx) => {
        return (
            <Tooltip
                placement="top"
                tooltipContent={
                    <div className="w-max px-3 py-1.5 text-sm">
                        <p className="max-w-120">
                            {tx.transaction_type.description}
                        </p>
                    </div>
                }
            >
                {({ setTriggerRef }) => (
                    <span className="dg-overflow" ref={setTriggerRef}>
                        {tx.transaction_type.name}
                    </span>
                )}
            </Tooltip>
        );
    },
};

export const NETWORK_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Network",
    slug: "network_name",
    type: ColumnTypes.string,
    width: 150,
    render: (tx) => {
        if (!tx.token || !tx.token.network) {
            return <Emdash />;
        }

        return (
            <Link
                variant="default"
                className="py-0.5 px-0.5 flex items-center"
                analyticsLabel={TransactionUserEvents.ClickNetworkLink}
                analyticsEventProperties={{
                    network: tx.token.network.slug,
                }}
                href={getNetworkURL({
                    networkSlug: tx.token.network.slug,
                })}
            >
                <NetworkBadge
                    network={tx.token.network}
                    enableTooltip={false}
                />
                <p className="ml-2 dg-overflow">{tx.token.network.name}</p>
            </Link>
        );
    },
};

export const AGE_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Age",
    slug: TransactionColumns.timestamp,
    sortsOn: TransactionColumns.timestamp,
    type: ColumnTypes.timeAgo,
    width: 135,
    cellValue: (tx) => {
        return tx.timestamp;
    },
};

export const FROM_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "From",
    slug: TransactionColumns.fromAddress,
    sortsOn: TransactionColumns.fromAddress,
    type: ColumnTypes.string,
    width: 175,
    maxWidth: 175,
    render: (tx, { size, width }) => {
        if (!tx.asset) {
            return <Emdash />;
        }

        return (
            <BlockchainExplorerLink
                address={tx.from_address}
                networkSlug={tx.token.network.slug}
                analyticsLabel={TransactionUserEvents.ClickFromAddress}
                size={size}
                type="address"
            />
        );
    },
};

export const TO_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "To",
    slug: TransactionColumns.toAddress,
    sortsOn: TransactionColumns.toAddress,
    type: ColumnTypes.string,
    width: 175,
    maxWidth: 175,
    render: (tx, { size, width }) => {
        if (!tx.asset) {
            return <Emdash />;
        }

        return (
            <BlockchainExplorerLink
                address={tx.to_address}
                networkSlug={tx.token.network.slug}
                analyticsLabel={TransactionUserEvents.ClickToAddress}
                size={size}
                type="address"
            />
        );
    },
};

export const AMOUNT_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Amount",
    slug: TransactionColumns.amount,
    sortsOn: TransactionColumns.amount,
    type: ColumnTypes.number,
    width: 150,
    cellValue: (tx) => {
        return tx.amount;
    },
};

export const USD_AMOUNT_COLUMN: ColumnMetadata<
    Transaction,
    TransactionColumns
> = {
    label: "USD Amount",
    slug: TransactionColumns.usdAmount,
    sortsOn: TransactionColumns.usdAmount,
    type: ColumnTypes.dollar,
    width: 150,
    cellValue: (tx) => {
        return tx.usd_amount;
    },
};

export const PROTOCOL_COLUMN: ColumnMetadata<Transaction, TransactionColumns> =
    {
        label: "Protocol",
        slug: "protocol_name",
        type: ColumnTypes.string,
        width: 200,
        render: (tx) => {
            const token = tx.token;

            // Return Emdash if no token
            if (!token || !token.protocol) return <Emdash />;

            // Return Emdash if no protocol
            const protocol = token.protocol;
            if (!protocol) return <Emdash />;

            return (
                <Link
                    href={getPlatformURL({ platformSlug: protocol.slug })}
                    analyticsLabel={TransactionUserEvents.ClickProtocolLink}
                    analyticsEventProperties={{
                        protocol: protocol.name,
                    }}
                    className="flex items-center gap-2"
                    variant="default"
                >
                    <ImageLoader
                        src={protocol.icon_url || RWA_COIN_ICON}
                        alt={`${protocol.name} Icon`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-5 w-5 min-w-5 rounded-full"
                    />
                    <div className="dg-overflow">{protocol.name}</div>
                </Link>
            );
        },
    };

export const ISSUER_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Issuer",
    slug: "issuer_name",
    type: ColumnTypes.string,
    width: 200,
    cellValue: (tx) => {
        if (!tx.asset || !tx.asset.issuer) {
            return null;
        }
        return tx.asset.issuer.name;
    },
};

export const ASSET_CLASS_COLUMN: ColumnMetadata<
    Transaction,
    TransactionColumns
> = {
    label: "Asset Class",
    slug: "asset_class_name",
    type: ColumnTypes.string,
    width: 150,
    render: (tx) => {
        if (!tx.asset || !tx.asset.asset_class) {
            return <Emdash />;
        }

        const {
            href,
            icon,
            label = tx.asset.asset_class_name,
        } = getAssetClassLinkMetadata(tx.asset.asset_class_id);

        if (href === null && label === null) {
            return <Emdash />;
        }

        if (href === null) {
            return <span className="dg-overflow">{label}</span>;
        }

        return (
            <Link
                href={href}
                analyticsLabel={TransactionUserEvents.ClickAssetClass}
                analyticsEventProperties={{
                    asset_class: tx.asset.asset_class_name,
                }}
                className="flex items-center gap-2"
                variant="default"
            >
                <Icon className="h-3.5" icon={icon} />
                <span className="dg-overflow">{label}</span>
            </Link>
        );
    },
};

export const TOKEN_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Token",
    slug: "token_address",
    type: ColumnTypes.string,
    width: 175,
    maxWidth: 175,
    render: (tx, { size, width }) => {
        if (!tx.asset) {
            return <Emdash />;
        }

        return (
            <BlockchainExplorerLink
                address={tx.token.address}
                networkSlug={tx.token.network.slug}
                analyticsLabel={TransactionUserEvents.ClickTokenAddress}
                size={size}
                type="token"
            />
        );
    },
};

export const DATE_COLUMN: ColumnMetadata<Transaction, TransactionColumns> = {
    label: "Date",
    slug: TransactionColumns.date,
    sortsOn: TransactionColumns.timestamp,
    type: ColumnTypes.date,
    width: 200,
    cellValue: (tx) => {
        return tx.timestamp;
    },
};

// export const TIMESTAMP_COLUMN: ColumnMetadata<Transaction, TransactionColumns> =
//     {
//         label: "Date",
//         slug: TransactionColumns.timestamp,
//         sortsOn: TransactionColumns.timestamp,
//         type: ColumnTypes.datetimeCompact,
//         width: 110,
//         visible: true,
//         cellValue: (tx) => {
//             return tx.timestamp;
//         },
//     };

export const TRANSACTION_TABLE_COLUMNS: ColumnMetadata<
    Transaction,
    TransactionColumns
>[] = [
    ASSET_COLUMN,
    TX_HASH_COLUMN,
    TYPE_COLUMN,
    NETWORK_COLUMN,
    AGE_COLUMN,
    FROM_COLUMN,
    TO_COLUMN,
    AMOUNT_COLUMN,
    USD_AMOUNT_COLUMN,
    PROTOCOL_COLUMN,
    ISSUER_COLUMN,
    ASSET_CLASS_COLUMN,
    TOKEN_COLUMN,
    DATE_COLUMN,
];

export const ASSET_INDIVIDUAL_TRANSACTION_TABLE_COLUMNS: ColumnMetadata<
    Transaction,
    TransactionColumns
>[] = TRANSACTION_TABLE_COLUMNS.slice(1);
